/** SS2 notification copy — normalizes stored legacy title/body text for display only. */

import { getFromStorage } from "./Helper";
import { StorageConstant } from "../constants/Constants";

type NotificationRow = {
  lang_id?: unknown;
  notification_params?: { title?: string; body?: string };
  data_params?: { type?: string; id?: string };
};

function extractOrderId(text: string, fallbackId?: string): string {
  const fromText = text.match(/order\s*id\s*:?\s*(\d+)/i)?.[1];
  return fromText || String(fallbackId || "").trim();
}

function extractQuoteId(text: string, fallbackId?: string): string {
  const fromText = text.match(/quote\s*id\s*:?\s*(\d+)/i)?.[1];
  return fromText || String(fallbackId || "").trim();
}

function isSpanishUi(row?: NotificationRow): boolean {
  const rowLang = row?.lang_id;
  if (rowLang != null && String(rowLang).trim() !== "") {
    return String(rowLang) === "2";
  }
  return (
    String(getFromStorage({ key: StorageConstant?.Translate }) || "").toLowerCase() ===
    "spa"
  );
}

export function formatNotificationTitle(row: NotificationRow): string {
  const raw = String(row?.notification_params?.title || "").trim();
  const type = String(row?.data_params?.type || "").trim().toLowerCase();
  const es = isSpanishUi(row);

  if (!raw) return raw;

  const normalized = raw.toLowerCase().replace(/\s+/g, " ").trim();

  if (normalized === "welcome to quote n buy" || normalized === "bienvenido a quote n buy") {
    return es ? "Bienvenido a Quote N Buy" : "Welcome to Quote N Buy";
  }

  if (
    normalized.includes("complete your profile") ||
    normalized.includes("completa tu perfil")
  ) {
    return es ? "Completa tu perfil" : "Complete your Profile";
  }

  if (
    type === "quotation" ||
    type === "quote" ||
    /^quote\s*id\s*:/i.test(raw) ||
    normalized.includes("your request has been quoted") ||
    normalized.includes("su solicitud ha sido cotizada") ||
    normalized.includes("admin react your provide quotation")
  ) {
    if (
      normalized.includes("rejected") ||
      normalized.includes("rechazad") ||
      normalized.includes("cancelled") ||
      normalized.includes("cancelad")
    ) {
      return es ? "Su solicitud ha sido rechazada" : "Your Request Has Been Rejected";
    }
    return es ? "Su solicitud ha sido cotizada" : "Your Request Has Been Quoted";
  }

  if (
    type === "order" ||
    normalized.includes("order in progress") ||
    normalized.includes("order in transit") ||
    normalized.includes("orden en progreso") ||
    normalized.includes("orden en transito") ||
    normalized.includes("your order has been delivered") ||
    normalized.includes("su pedido ha sido entregado") ||
    normalized.includes("order status has been updated") ||
    normalized.includes("estado del pedido actualizado")
  ) {
    return es ? "Estado del pedido actualizado" : "Order Status Has Been Updated";
  }

  if (normalized.includes("your quotation has been cancelled")) {
    return es ? "Su solicitud ha sido rechazada" : "Your Request Has Been Rejected";
  }

  return raw;
}

export function formatNotificationBody(row: NotificationRow): string {
  const raw = String(row?.notification_params?.body || "").trim();
  const title = String(row?.notification_params?.title || "").trim();
  const type = String(row?.data_params?.type || "").trim().toLowerCase();
  const refId = row?.data_params?.id;
  const es = isSpanishUi(row);

  if (!raw) return raw;

  const normalized = raw.toLowerCase().replace(/\s+/g, " ").trim();
  const normalizedTitle = title.toLowerCase().replace(/\s+/g, " ").trim();

  if (
    normalizedTitle.includes("complete your profile") ||
    normalizedTitle.includes("completa tu perfil")
  ) {
    return es
      ? "Solo un paso más, complete su perfil para comenzar."
      : "Just one more step, complete your profile to get started.";
  }

  const quoteId = extractQuoteId(`${title} ${raw}`, refId);
  if (
    type === "quotation" ||
    type === "quote" ||
    /^quote\s*id\s*:/i.test(raw) ||
    normalized.includes("ready to view") ||
    normalized.includes("lista para ver")
  ) {
    if (
      normalizedTitle.includes("rejected") ||
      normalizedTitle.includes("rechazad") ||
      normalized.includes("rejected by admin") ||
      normalized.includes("rechazad")
    ) {
      if (raw.length > 0 && !/^quote\s*id/i.test(raw)) {
        return raw;
      }
      return es
        ? "La solicitud ha sido rechazada por el administrador. Debido a artículo agotado."
        : "Request has been rejected by admin. Due to out of stock item.";
    }
    if (quoteId) {
      return es
        ? `ID de cotización: ${quoteId} Está lista para ver`
        : `Quote ID: ${quoteId} Is ready to view`;
    }
  }

  const orderId = extractOrderId(`${title} ${raw}`, refId);
  if (orderId && (type === "order" || /order\s*id/i.test(raw))) {
    if (
      normalized.includes("delivered") ||
      normalized.includes("entregad")
    ) {
      return es
        ? `ID de pedido: ${orderId} Ha sido entregado. ¡Déjanos una reseña aquí, gracias!`
        : `Order ID: ${orderId} Has been delivered. Leave us a review here, thank you!`;
    }
    if (
      normalized.includes("in transit") ||
      normalized.includes("en tránsito") ||
      normalized.includes("en transito")
    ) {
      return es
        ? `ID de pedido: ${orderId} Está en tránsito`
        : `Order ID: ${orderId} Is in transit`;
    }
    if (
      normalized.includes("in process") ||
      normalized.includes("in progress") ||
      normalized.includes("en proceso") ||
      normalized.includes("procesado")
    ) {
      return es
        ? `ID de pedido: ${orderId} Está en proceso`
        : `Order ID: ${orderId} Is in process`;
    }
    if (
      normalized.includes("payment was declined") ||
      normalized.includes("pago fue rechazado")
    ) {
      return es
        ? `ID de pedido: ${orderId} Pedido pendiente, el pago fue rechazado. Revise los detalles e inténtelo de nuevo.`
        : `Order ID: ${orderId} Order pending, payment was declined. Please check details and try again.`;
    }
    if (
      normalized.includes("payment was rejected") ||
      normalized.includes("pago fue rechazado")
    ) {
      return es
        ? `ID de pedido: ${orderId} Pedido cancelado, el pago fue rechazado. Revise los detalles e inténtelo de nuevo.`
        : `Order ID: ${orderId} Order cancelled, payment was rejected. Please check details and try again.`;
    }
  }

  return raw;
}
